import { useEffect, useState } from 'react';

import {
  ALL_INVOICE_DISPLAY_STATUSES,
  INVOICE_STATUS_COLORS,
  INVOICE_STATUS_LABELS,
  type InvoiceDisplayStatus,
} from './invoiceLabels';
import { IconSearch, IconX } from '../incidents/icons';

interface InvoicesFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  status: InvoiceDisplayStatus[];
  onStatusChange: (next: InvoiceDisplayStatus[]) => void;
  totalCount: number;
}

export const InvoicesFilters = ({
  search,
  onSearchChange,
  status,
  onStatusChange,
  totalCount,
}: InvoicesFiltersProps) => {
  const [draft, setDraft] = useState(search);

  useEffect(() => {
    setDraft(search);
  }, [search]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      if (draft !== search) onSearchChange(draft);
    }, 300);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  const toggleStatus = (s: InvoiceDisplayStatus) => {
    onStatusChange(status.includes(s) ? status.filter(x => x !== s) : [...status, s]);
  };

  const hasFilter = search.length > 0 || status.length > 0;

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="relative flex-1 max-w-xs">
        <IconSearch
          width={14}
          height={14}
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none"
        />
        <input
          type="text"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder="N° facture, entité ou contrat"
          className="w-full h-8 pl-8 pr-8 text-sm bg-white border border-zinc-200 rounded-md text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/5 transition-colors"
        />
        {draft ? (
          <button
            type="button"
            onClick={() => {
              setDraft('');
              onSearchChange('');
            }}
            aria-label="Effacer la recherche"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 h-5 w-5 inline-flex items-center justify-center rounded text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
          >
            <IconX width={12} height={12} />
          </button>
        ) : null}
      </div>

      <div className="flex items-center gap-1.5">
        {ALL_INVOICE_DISPLAY_STATUSES.map(s => {
          const active = status.includes(s);
          const tokens = INVOICE_STATUS_COLORS[s];
          return (
            <button
              key={s}
              type="button"
              onClick={() => toggleStatus(s)}
              className={[
                'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs font-medium transition-colors duration-100 active:scale-[0.98]',
                active
                  ? 'bg-zinc-900 text-white border border-zinc-900'
                  : 'bg-white text-zinc-700 border border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50',
              ].join(' ')}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-white/80' : tokens.dot}`} aria-hidden />
              {INVOICE_STATUS_LABELS[s]}
            </button>
          );
        })}
      </div>

      <div className="ml-auto flex items-center gap-3 text-xs text-zinc-500">
        {hasFilter ? (
          <button
            type="button"
            onClick={() => {
              setDraft('');
              onSearchChange('');
              onStatusChange([]);
            }}
            className="text-zinc-500 hover:text-zinc-900 underline-offset-4 hover:underline transition-colors"
          >
            Effacer les filtres
          </button>
        ) : null}
        <span className="font-mono tabular-nums">
          {totalCount} <span className="text-zinc-400">facture{totalCount > 1 ? 's' : ''}</span>
        </span>
      </div>
    </div>
  );
};
